// Iteration 1.14 — client-side game-mode state machine.
//
// The Coordinator owns the authoritative state machine. This module is the
// thin client mirror that:
//   - Tracks which mode the server has pushed us into.
//   - Acks every server-pushed transition.
//   - Lets the user-driven UI request mode transitions.
//   - Notifies subscribers (renderer, input, main loop) on mode changes.
//
// The module is a singleton: one connection, one state machine.

export const GameMode = Object.freeze({
  ARENA_LOBBY: 'arena_lobby',
  ARENA: 'arena',
})

let current = GameMode.ARENA_LOBBY
let lastAckedSeq = 0
let lastSentSeq = 0
const listeners = new Set()
let socketRef = null

// configureModeBridge wires the WebSocket the module should write acks /
// requests to. Called once after the WS opens.
export function configureModeBridge(socket) {
  socketRef = socket
}

export function getGameMode() {
  return current
}

// onModeChange registers a listener that fires (mode, prev, seq) on every
// applied transition. Returns an unregister function.
export function onModeChange(fn) {
  listeners.add(fn)
  return () => listeners.delete(fn)
}

// applyServerMode is the entry point for incoming `set_game_mode` frames.
// Updates the current mode, notifies listeners, and writes the ack on the
// configured socket.
export function applyServerMode(mode, seq) {
  if (mode !== GameMode.ARENA_LOBBY && mode !== GameMode.ARENA) {
    return
  }
  const prev = current
  current = mode
  lastAckedSeq = seq | 0
  for (const fn of listeners) {
    try {
      fn(mode, prev, seq | 0)
    } catch {
      /* listener errors must not break the dispatcher */
    }
  }
  sendJSON(socketRef, { kind: 'set_game_mode_ack', mode, seq: seq | 0 })
}

// requestMode is the client-initiated path. Called from the lobby Start
// button. Mints a strictly-increasing seq from the client side (Coordinator
// also tracks one server-side; the client seq is logged but not load-bearing).
export function requestMode(mode) {
  const seq = nextSeq()
  sendJSON(socketRef, { kind: 'request_game_mode', mode, seq })
  return seq
}

// Test-only: reset module state. Lets each test start from a clean module.
export function _resetForTesting() {
  current = GameMode.ARENA_LOBBY
  lastAckedSeq = 0
  lastSentSeq = 0
  listeners.clear()
  socketRef = null
}

// Test-only: read the last seq the module acked.
export function _lastAckedSeq() {
  return lastAckedSeq
}

function nextSeq() {
  lastSentSeq = (lastSentSeq + 1) | 0
  return lastSentSeq
}

function sendJSON(ws, obj) {
  if (!ws || ws.readyState !== 1 /* OPEN */) return
  try {
    ws.send(JSON.stringify(obj))
  } catch {
    /* WS may be closing */
  }
}
